import { useState, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  alpha,
  Divider,
  Radio,
  RadioGroup,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import axiosInstance from '../lib/axios';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { palette, typography } from '../theme/colors';
import { User, Delivery } from '../types';

interface DriverAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  delivery: Delivery | null;
  onAssigned?: (driverId: string) => void;
}

interface DriverWithStats extends User {
  activeDeliveries?: number;
  completedToday?: number;
  isAvailable?: boolean;
}

const fetchDrivers = async (): Promise<DriverWithStats[]> => {
  // Use dedicated /users/drivers endpoint which filters by userType=platform_staff AND roleSystemKey=driver
  const response = await axiosInstance.get('/users/drivers', {
    params: {
      limit: 100,
    },
  });

  const users = Array.isArray(response.data) ? response.data : response.data.data || [];

  // For now, return with mock stats - in production this would come from the API
  return users.map((user: User) => ({
    ...user,
    activeDeliveries: Math.floor(Math.random() * 5),
    completedToday: Math.floor(Math.random() * 10),
    isAvailable: Math.random() > 0.3,
  }));
};

const assignDriver = async ({
  deliveryId,
  driverId,
}: {
  deliveryId: string;
  driverId: string;
}): Promise<Delivery> => {
  const response = await axiosInstance.patch(`/deliveries/${deliveryId}/assign`, {
    driverId,
  });
  return response.data;
};

export const DriverAssignmentModal: React.FC<DriverAssignmentModalProps> = ({
  open,
  onClose,
  delivery,
  onAssigned,
}) => {
  const { mode } = useContext(ColorModeContext);
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(
    delivery?.driverId || null
  );

  const { data: drivers = [], isLoading } = useQuery({
    queryKey: ['drivers'],
    queryFn: fetchDrivers,
    enabled: open,
  });

  const assignMutation = useMutation({
    mutationFn: assignDriver,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      queryClient.invalidateQueries({ queryKey: ['delivery', delivery?.id] });
      onAssigned?.(selectedDriverId!);
      onClose();
    },
  });

  const filteredDrivers = drivers.filter((driver) => {
    if (!searchValue) return true;
    const search = searchValue.toLowerCase();
    return (
      driver.firstName?.toLowerCase().includes(search) ||
      driver.lastName?.toLowerCase().includes(search) ||
      driver.email?.toLowerCase().includes(search) ||
      driver.phone?.toLowerCase().includes(search)
    );
  });

  // Sort by availability, then by active deliveries (ascending)
  const sortedDrivers = [...filteredDrivers].sort((a, b) => {
    if (a.isAvailable !== b.isAvailable) {
      return a.isAvailable ? -1 : 1;
    }
    return (a.activeDeliveries || 0) - (b.activeDeliveries || 0);
  });

  const handleAssign = () => {
    if (!delivery || !selectedDriverId) return;
    assignMutation.mutate({
      deliveryId: delivery.id,
      driverId: selectedDriverId,
    });
  };

  const getDriverInitials = (driver: User) => {
    return `${driver.firstName?.[0] || ''}${driver.lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: mode === 'dark' ? palette.background : palette.light.background,
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: alpha(palette.primary, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DirectionsCarIcon sx={{ color: palette.primary }} />
        </Box>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.semibold,
            }}
          >
            Assign Driver
          </Typography>
          {delivery && (
            <Typography variant="body2" color="text.secondary">
              Delivery #{delivery.id.slice(0, 8)}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* Delivery Summary */}
        {delivery && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              bgcolor: mode === 'dark' ? alpha('#fff', 0.02) : alpha('#000', 0.02),
              border: `1px solid ${mode === 'dark' ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocalShippingIcon sx={{ fontSize: 18, color: palette.info }} />
              <Typography variant="subtitle2">Delivery Details</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Customer:</strong> {delivery.customerName || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Address:</strong>{' '}
              {delivery.deliveryAddress
                ? `${delivery.deliveryAddress.street}, ${delivery.deliveryAddress.city}`
                : 'N/A'}
            </Typography>
          </Box>
        )}

        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search drivers by name, email, or phone..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: palette.textMuted }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Driver List */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : sortedDrivers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <PersonIcon sx={{ fontSize: 48, color: palette.textMuted, mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No drivers found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchValue
                ? 'Try adjusting your search'
                : 'No drivers are available in your organization'}
            </Typography>
          </Box>
        ) : (
          <RadioGroup
            value={selectedDriverId || ''}
            onChange={(e) => setSelectedDriverId(e.target.value)}
          >
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {sortedDrivers.map((driver, index) => (
                <ListItem
                  key={driver.id}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    border: `1px solid ${
                      selectedDriverId === driver.id
                        ? palette.primary
                        : mode === 'dark'
                          ? alpha('#fff', 0.1)
                          : alpha('#000', 0.1)
                    }`,
                    bgcolor:
                      selectedDriverId === driver.id
                        ? alpha(palette.primary, 0.05)
                        : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(palette.primary, 0.05),
                    },
                  }}
                  onClick={() => setSelectedDriverId(driver.id)}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: driver.isAvailable
                          ? alpha(palette.success, 0.1)
                          : alpha(palette.textSecondary, 0.1),
                        color: driver.isAvailable
                          ? palette.success
                          : palette.textSecondary,
                      }}
                    >
                      {getDriverInitials(driver)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">
                          {driver.firstName} {driver.lastName}
                        </Typography>
                        <Chip
                          size="small"
                          label={driver.isAvailable ? 'Available' : 'Busy'}
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: driver.isAvailable
                              ? alpha(palette.success, 0.1)
                              : alpha(palette.warning, 0.1),
                            color: driver.isAvailable
                              ? palette.success
                              : palette.warning,
                          }}
                        />
                        {delivery?.driverId === driver.id && (
                          <Chip
                            size="small"
                            label="Current"
                            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              bgcolor: alpha(palette.primary, 0.1),
                              color: palette.primary,
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <EmailIcon sx={{ fontSize: 12 }} />
                          {driver.email}
                        </Typography>
                        {driver.phone && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}
                          >
                            <PhoneIcon sx={{ fontSize: 12 }} />
                            {driver.phone}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Active: <strong>{driver.activeDeliveries}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Today: <strong>{driver.completedToday}</strong>
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Radio
                      checked={selectedDriverId === driver.id}
                      value={driver.id}
                      sx={{
                        color: palette.textMuted,
                        '&.Mui-checked': {
                          color: palette.primary,
                        },
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </RadioGroup>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={!selectedDriverId || assignMutation.isPending}
          startIcon={
            assignMutation.isPending ? (
              <CircularProgress size={16} />
            ) : (
              <CheckCircleIcon />
            )
          }
          sx={{
            bgcolor: palette.primary,
            '&:hover': {
              bgcolor: alpha(palette.primary, 0.9),
            },
          }}
        >
          {assignMutation.isPending ? 'Assigning...' : 'Assign Driver'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriverAssignmentModal;
